/**
 * @file components/chat/MarkdownText.tsx
 * 7.3-01 — Rendu Markdown sécurisé des bulles agent.
 *
 * Sanitisation stricte via rehype-sanitize (anti-XSS) : seuls
 * a/strong/em/ul/ol/li/code/pre/p/br sont autorisés. Tout HTML brut est
 * neutralisé. Les liens s'ouvrent dans un nouvel onglet (noopener noreferrer).
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';

/** Schéma de sanitisation strict — liste blanche minimale (ticket 7.3-01). */
const sanitizeSchema: typeof defaultSchema = {
  ...defaultSchema,
  tagNames: ['a', 'strong', 'em', 'ul', 'ol', 'li', 'code', 'pre', 'p', 'br'],
  attributes: {
    a: ['href'],
    code: [['className', /^language-/]],
  },
  protocols: { href: ['http', 'https', 'mailto'] },
};

const remarkPlugins = [remarkGfm];
const rehypePlugins = [[rehypeSanitize, sanitizeSchema] as const];

/**
 * Texte Markdown stylé "prose compacte" (Tailwind), cohérent avec le thème
 * des bulles (text-sm, leading-relaxed). Utilisé uniquement côté agent —
 * les bulles utilisateur restent en texte brut (whitespace-pre-wrap).
 */
export default function MarkdownText({ text }: { text: string }): React.ReactElement {
  return (
    <div className="break-words text-sm leading-relaxed [&>*+*]:mt-2">
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins as never}
        components={{
          a: ({ node, ...props }) => (
            <a
              {...props}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline underline-offset-2 hover:opacity-80"
            />
          ),
          ul: ({ node, ...props }) => (
            <ul {...props} className="list-disc space-y-1 pl-5" />
          ),
          ol: ({ node, ...props }) => (
            <ol {...props} className="list-decimal space-y-1 pl-5" />
          ),
          pre: ({ node, ...props }) => (
            <pre
              {...props}
              className="overflow-x-auto rounded-lg bg-muted px-3 py-2 text-[12.5px] leading-snug"
            />
          ),
          code: ({ node, ...props }) => (
            <code {...props} className="rounded bg-muted px-1 py-0.5 font-mono text-[12.5px]" />
          ),
          strong: ({ node, ...props }) => <strong {...props} className="font-semibold" />,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
